/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — source of truth lives at
 *   cds-ai-service/frontend/src/views/dev-chat/chat-stream/chatMarkdownComponents.jsx
 * See ./README.md for the extraction plan.
 */

import { useRef } from 'react';

import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Link as MuiLink } from '@mui/material';

const sanitizeMarkdownProps = ({ node, ordered, checked, ...rest } = {}) => {
  void node;
  void ordered;
  void checked;

  return rest;
};

const INHERIT_FONT = { fontFamily: 'inherit' };

const COMFORTABLE_TYPOGRAPHY = {
  heading: {
    h1: {
      ...INHERIT_FONT,
      fontSize: '1.375rem',
      fontWeight: 700,
      mt: 3,
      mb: 1.25,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h2: {
      ...INHERIT_FONT,
      fontSize: '1.2rem',
      fontWeight: 700,
      mt: 2.5,
      mb: 1,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: { ...INHERIT_FONT, fontSize: '1.05rem', fontWeight: 700, mt: 2, mb: 0.75, lineHeight: 1.35 },
    h4: { ...INHERIT_FONT, fontSize: '0.975rem', fontWeight: 600, mt: 1.5, mb: 0.5, lineHeight: 1.4 },
    h5: { ...INHERIT_FONT, fontSize: '0.9rem', fontWeight: 600, mt: 1.25, mb: 0.5, lineHeight: 1.4 },
    h6: { ...INHERIT_FONT, fontSize: '0.85rem', fontWeight: 600, mt: 1.25, mb: 0.5, lineHeight: 1.4 },
  },
  paragraph: { ...INHERIT_FONT, my: 1, lineHeight: 1.6 },
  list: { ...INHERIT_FONT, pl: 3, my: 1 },
  listItem: { ...INHERIT_FONT, mt: 0.25, lineHeight: 1.6 },
  blockquote: { ...INHERIT_FONT, pl: 1.75, my: 1.5, borderLeftWidth: 3 },
  divider: { my: 2 },
};

const COMPACT_TYPOGRAPHY = {
  heading: {
    h1: {
      ...INHERIT_FONT,
      fontSize: '1.125rem',
      fontWeight: 700,
      mt: 2,
      mb: 0.75,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h2: {
      ...INHERIT_FONT,
      fontSize: '1rem',
      fontWeight: 700,
      mt: 1.75,
      mb: 0.5,
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    h3: { ...INHERIT_FONT, fontSize: '0.925rem', fontWeight: 700, mt: 1.5, mb: 0.5, lineHeight: 1.35 },
    h4: { ...INHERIT_FONT, fontSize: '0.875rem', fontWeight: 600, mt: 1.25, mb: 0.25, lineHeight: 1.4 },
    h5: { ...INHERIT_FONT, fontSize: '0.825rem', fontWeight: 600, mt: 1, mb: 0.25, lineHeight: 1.4 },
    h6: { ...INHERIT_FONT, fontSize: '0.8rem', fontWeight: 600, mt: 1, mb: 0.25, lineHeight: 1.4 },
  },
  paragraph: { ...INHERIT_FONT, my: 0.75, lineHeight: 1.55, fontSize: '0.9rem' },
  list: { ...INHERIT_FONT, pl: 2.25, my: 0.75 },
  listItem: { ...INHERIT_FONT, mt: 0.15, lineHeight: 1.55, fontSize: '0.9rem' },
  blockquote: { ...INHERIT_FONT, pl: 1.25, my: 1, borderLeftWidth: 2 },
  divider: { my: 1.5 },
};

const resolveTypography = density => (density === 'compact' ? COMPACT_TYPOGRAPHY : COMFORTABLE_TYPOGRAPHY);

const buildHeadingRenderer = (level, density) => {
  const typography = resolveTypography(density);
  const headingStyles = typography.heading[level];

  return props => {
    const { children, ...rest } = sanitizeMarkdownProps(props);

    return (
      <Box
        component={level}
        {...rest}
        sx={{
          ...headingStyles,
          '&:first-of-type': { mt: 0 },
        }}
      >
        {children}
      </Box>
    );
  };
};

const CodeBlockWithCopy = ({ children, theme }) => {
  const preRef = useRef(null);

  const handleCopy = () => {
    const text = preRef.current?.textContent ?? '';

    if (text && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '10px',
        backgroundColor: theme.validateMode({
          light: 'rgba(15, 23, 42, 0.04)',
          dark: 'rgba(148, 163, 184, 0.12)',
        }),
        overflow: 'hidden',
        my: 1.25,
      }}
    >
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: theme.validateMode({ light: '#64748b', dark: '#94a3b8' }),
          '&:hover': {
            backgroundColor: theme.validateMode({
              light: 'rgba(15, 23, 42, 0.06)',
              dark: 'rgba(148, 163, 184, 0.15)',
            }),
          },
        }}
        aria-label="Copy code"
      >
        <ContentCopy sx={{ fontSize: 16 }} />
      </IconButton>
      <Box
        ref={preRef}
        component="pre"
        sx={{
          overflowX: 'auto',
          p: 1.5,
          pr: 5,
          m: 0,
          fontSize: '0.85rem',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export const createChatMarkdownComponents = (theme, options = {}) => {
  const { density = 'comfortable' } = options;
  const typography = resolveTypography(density);
  const borderColor = theme.validateMode({
    light: 'rgba(15, 23, 42, 0.12)',
    dark: 'rgba(148, 163, 184, 0.2)',
  });
  const mutedTextColor = theme.validateMode({ light: '#64748b', dark: '#94a3b8' });

  return {
    h1: buildHeadingRenderer('h1', density),
    h2: buildHeadingRenderer('h2', density),
    h3: buildHeadingRenderer('h3', density),
    h4: buildHeadingRenderer('h4', density),
    h5: buildHeadingRenderer('h5', density),
    h6: buildHeadingRenderer('h6', density),
    p: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box
          component="p"
          {...rest}
          sx={{
            ...typography.paragraph,
            '&:first-of-type': { mt: 0 },
            '&:last-of-type': { mb: 0 },
          }}
        >
          {children}
        </Box>
      );
    },
    ul: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box
          component="ul"
          {...rest}
          sx={{
            ...typography.list,
            listStyleType: 'disc',
            '&:first-of-type': { mt: 0 },
            '&:last-of-type': { mb: 0 },
          }}
        >
          {children}
        </Box>
      );
    },
    ol: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box
          component="ol"
          {...rest}
          sx={{
            ...typography.list,
            listStyleType: 'decimal',
            '&:first-of-type': { mt: 0 },
            '&:last-of-type': { mb: 0 },
          }}
        >
          {children}
        </Box>
      );
    },
    li: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box
          component="li"
          {...rest}
          sx={{
            ...typography.listItem,
            '& > p': { my: 0 },
          }}
        >
          {children}
        </Box>
      );
    },
    strong: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box component="strong" {...rest} sx={{ fontWeight: 600 }}>
          {children}
        </Box>
      );
    },
    em: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box component="em" {...rest} sx={{ fontStyle: 'italic' }}>
          {children}
        </Box>
      );
    },
    a: props => {
      const { children, href, ...rest } = sanitizeMarkdownProps(props);

      return (
        <MuiLink
          href={href}
          target={href && /^https?:\/\//.test(href) ? '_blank' : undefined}
          rel={href && /^https?:\/\//.test(href) ? 'noopener noreferrer' : undefined}
          underline="hover"
          color="primary"
          {...rest}
        >
          {children}
        </MuiLink>
      );
    },
    blockquote: props => {
      const { children, ...rest } = sanitizeMarkdownProps(props);

      return (
        <Box
          component="blockquote"
          {...rest}
          sx={{
            ...typography.blockquote,
            borderLeftStyle: 'solid',
            borderLeftColor: borderColor,
            color: mutedTextColor,
            fontStyle: 'italic',
            ml: 0,
            '& > :first-of-type': { mt: 0 },
            '& > :last-of-type': { mb: 0 },
          }}
        >
          {children}
        </Box>
      );
    },
    hr: props => {
      const rest = sanitizeMarkdownProps(props);

      return (
        <Box
          component="hr"
          {...rest}
          sx={{
            border: 0,
            borderTop: `1px solid ${borderColor}`,
            ...typography.divider,
          }}
        />
      );
    },
    pre: ({ children }) => <CodeBlockWithCopy theme={theme}>{children}</CodeBlockWithCopy>,
    code: ({ children, className, ...props }) => {
      const isInline = !className;

      if (isInline) {
        return (
          <Box
            component="code"
            sx={{
              px: 0.5,
              py: 0.1,
              borderRadius: '6px',
              fontSize: '0.86em',
              backgroundColor: theme.validateMode({
                light: 'rgba(15, 23, 42, 0.06)',
                dark: 'rgba(148, 163, 184, 0.16)',
              }),
            }}
          >
            {children}
          </Box>
        );
      }

      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    table: ({ children }) => (
      <Box
        sx={{
          overflowX: 'auto',
          my: 2,
          borderRadius: '8px',
          border: `1px solid ${theme.validateMode({
            light: 'rgba(15, 23, 42, 0.12)',
            dark: 'rgba(148, 163, 184, 0.2)',
          })}`,
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            '& th, & td': {
              px: 1.5,
              py: 1,
              textAlign: 'left',
              borderBottom: `1px solid ${theme.validateMode({
                light: 'rgba(15, 23, 42, 0.08)',
                dark: 'rgba(148, 163, 184, 0.15)',
              })}`,
            },
            '& th': {
              fontWeight: 600,
              backgroundColor: theme.validateMode({
                light: 'rgba(15, 23, 42, 0.04)',
                dark: 'rgba(148, 163, 184, 0.08)',
              }),
              color: theme.validateMode({
                light: '#334155',
                dark: '#e2e8f0',
              }),
            },
            '& td': {
              color: theme.validateMode({
                light: '#475569',
                dark: '#cbd5e1',
              }),
            },
            '& tr:last-child td': {
              borderBottom: 'none',
            },
            '& tr:hover td': {
              backgroundColor: theme.validateMode({
                light: 'rgba(15, 23, 42, 0.02)',
                dark: 'rgba(148, 163, 184, 0.05)',
              }),
            },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }) => <Box component="thead">{children}</Box>,
    tbody: ({ children }) => <Box component="tbody">{children}</Box>,
    tr: ({ children }) => <Box component="tr">{children}</Box>,
    th: ({ children }) => <Box component="th">{children}</Box>,
    td: ({ children }) => <Box component="td">{children}</Box>,
  };
};
